import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Calendar, MessageCircle, Check, Bell, 
  Users, TrendingUp, Settings,
  Phone, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { siteData } from '../data/mock';
import { formatPrice, formatDuration } from '../data/agendaMock';

// Gerar agendamentos de demonstração (fora do componente)
const generateDemoAppointments = () => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return [
    {
      id: 1,
      client: { name: 'João Silva', phone: '24999991111' },
      service: { name: 'Corte Masculino', duration: 30, price: 35 },
      date: today.toISOString(),
      time: '09:00',
      status: 'confirmed'
    },
    {
      id: 2,
      client: { name: 'Maria Santos', phone: '24999992222' },
      service: { name: 'Barba', duration: 20, price: 25 },
      date: today.toISOString(),
      time: '10:00',
      status: 'confirmed'
    },
    {
      id: 3,
      client: { name: 'Pedro Oliveira', phone: '24999993333' },
      service: { name: 'Corte + Barba', duration: 45, price: 55 },
      date: today.toISOString(),
      time: '14:00',
      status: 'pending'
    },
    {
      id: 4,
      client: { name: 'Ana Costa', phone: '24999994444' },
      service: { name: 'Corte Masculino', duration: 30, price: 35 },
      date: today.toISOString(),
      time: '15:30',
      status: 'confirmed'
    },
    {
      id: 5,
      client: { name: 'Lucas Mendes', phone: '24999995555' },
      service: { name: 'Pigmentação', duration: 40, price: 45 },
      date: tomorrow.toISOString(),
      time: '09:00',
      status: 'confirmed'
    },
    {
      id: 6,
      client: { name: 'Carla Ferreira', phone: '24999996666' },
      service: { name: 'Corte + Barba', duration: 45, price: 55 },
      date: tomorrow.toISOString(),
      time: '11:00',
      status: 'pending'
    }
  ];
};

const PainelProfissional = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [professional, setProfessional] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('hoje');
  const [sentReminders, setSentReminders] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Carregar dados do profissional do backend ou localStorage
      let prof = null;
      try {
        const response = await fetch(`${API_URL}/api/agenda/professionals/${slug}`);
        if (response.ok) {
          prof = await response.json();
        }
      } catch (e) {
        console.log('Fallback to localStorage for professional');
      }
      
      if (!prof) {
        const stored = localStorage.getItem(`agenda_${slug}`);
        prof = stored ? JSON.parse(stored) : null;
      }

      // Carregar agendamentos do backend
      let appts = [];
      try {
        const todayResp = await fetch(`${API_URL}/api/agenda/appointments/${slug}/today`);
        const tomorrowResp = await fetch(`${API_URL}/api/agenda/appointments/${slug}/tomorrow`);
        
        if (todayResp.ok && tomorrowResp.ok) {
          const todayAppts = await todayResp.json();
          const tomorrowAppts = await tomorrowResp.json();
          appts = [...todayAppts, ...tomorrowAppts];
        }
      } catch (e) {
        console.log('Fallback to localStorage/demo for appointments');
      }
      
      // Se não houver agendamentos do backend, usar localStorage ou demo
      if (appts.length === 0) {
        const storedAppointments = localStorage.getItem(`appointments_${slug}`);
        appts = storedAppointments ? JSON.parse(storedAppointments) : generateDemoAppointments();
      }

      // Carregar lembretes já enviados
      const storedReminders = localStorage.getItem(`sent_reminders_${slug}`);
      const reminders = storedReminders ? JSON.parse(storedReminders) : [];

      setProfessional(prof);
      setAppointments(appts);
      setSentReminders(reminders);
      setLoading(false);
    };

    loadData();
  }, [slug]);

  // Filtrar agendamentos por data
  const getTodayAppointments = () => {
    const today = new Date().toISOString().split('T')[0]; // "2025-01-07"
    return appointments.filter(apt => {
      // Suporta formato antigo (date como ISO string) e novo (date como "2025-01-07")
      const aptDate = apt.date?.includes('T') ? apt.date.split('T')[0] : apt.date;
      return aptDate === today;
    });
  };

  const getTomorrowAppointments = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    return appointments.filter(apt => {
      const aptDate = apt.date?.includes('T') ? apt.date.split('T')[0] : apt.date;
      return aptDate === tomorrowStr;
    });
  };

  // Verificar se lembrete já foi enviado
  const isReminderSent = (appointmentId, type) => {
    return sentReminders.some(r => r.appointmentId === appointmentId && r.type === type);
  };

  // Enviar lembrete via WhatsApp
  const handleSendReminder = (appointment, type) => {
    const businessName = professional?.business || 'nosso estabelecimento';
    const formattedDate = new Date(appointment.date).toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });

    let message = '';
    if (type === '24h') {
      message = `Olá ${appointment.client.name}! 👋\n\n` +
        `Lembrete: Você tem *${appointment.service.name}* agendado para *amanhã* às *${appointment.time}* na *${businessName}*.\n\n` +
        `📍 Endereço: ${professional?.address || 'A confirmar'}\n\n` +
        `Confirma presença? Responda SIM ou NÃO.\n\n` +
        `Até lá! 😊`;
    } else {
      message = `Olá ${appointment.client.name}! ⏰\n\n` +
        `Lembrete: Seu horário é *HOJE às ${appointment.time}*!\n\n` +
        `📍 *${appointment.service.name}* na *${businessName}*\n\n` +
        `Te esperamos! 😊`;
    }

    // Abrir WhatsApp
    const phone = appointment.client.phone.replace(/\D/g, '');
    window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(message)}`, '_blank');

    // Marcar como enviado
    const newReminder = {
      appointmentId: appointment.id,
      type,
      sentAt: new Date().toISOString()
    };
    const updatedReminders = [...sentReminders, newReminder];
    setSentReminders(updatedReminders);
    localStorage.setItem(`sent_reminders_${slug}`, JSON.stringify(updatedReminders));
  };

  // Calcular estatísticas
  const getStats = () => {
    const today = getTodayAppointments();
    const tomorrow = getTomorrowAppointments();
    const totalRevenue = today.reduce((sum, apt) => sum + apt.service.price, 0);
    
    return {
      todayCount: today.length,
      tomorrowCount: tomorrow.length,
      todayRevenue: totalRevenue,
      pendingReminders: tomorrow.filter(apt => !isReminderSent(apt.id, '24h')).length
    };
  };

  // Formatar hora atual
  const formatTime = (date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-slate-50">
        <AlertCircle size={48} className="text-amber-500 mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Painel não encontrado</h1>
        <p className="text-slate-600 mb-6">Configure sua agenda primeiro.</p>
        <Button onClick={() => navigate('/agenda/configurar')} className="btn-primary">
          Configurar Agenda
        </Button>
      </div>
    );
  }

  const stats = getStats();
  const todayAppointments = getTodayAppointments();
  const tomorrowAppointments = getTomorrowAppointments();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2">
              <img src={siteData.brand.logo} alt="AtivaJá" className="h-8" />
            </a>
            <span className="text-slate-300">|</span>
            <div>
              <h1 className="font-semibold text-slate-900">{professional.business}</h1>
              <p className="text-xs text-slate-500">Painel de Controle</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate(`/agenda/${slug}`)}
            >
              Ver minha página
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/agenda/configurar')}
            >
              <Settings size={18} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Cards de estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.todayCount}</p>
                <p className="text-xs text-slate-500">Hoje</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatPrice(stats.todayRevenue)}</p>
                <p className="text-xs text-slate-500">Receita hoje</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Users size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.tomorrowCount}</p>
                <p className="text-xs text-slate-500">Amanhã</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                stats.pendingReminders > 0 ? 'bg-amber-100' : 'bg-green-100'
              }`}>
                <Bell size={20} className={stats.pendingReminders > 0 ? 'text-amber-600' : 'text-green-600'} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.pendingReminders}</p>
                <p className="text-xs text-slate-500">Lembretes pendentes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('hoje')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'hoje'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            Hoje ({stats.todayCount})
          </button>
          <button
            onClick={() => setActiveTab('amanha')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'amanha'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            Amanhã ({stats.tomorrowCount})
            {stats.pendingReminders > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                {stats.pendingReminders}
              </span>
            )}
          </button>
        </div>

        {/* Lista de agendamentos */}
        <div className="space-y-4">
          {activeTab === 'hoje' && (
            <>
              {todayAppointments.length === 0 ? (
                <div className="bg-white rounded-xl p-8 border border-slate-200 text-center">
                  <Calendar size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nenhum agendamento para hoje</p>
                </div>
              ) : (
                todayAppointments.map((apt) => (
                  <div 
                    key={apt.id}
                    className="bg-white rounded-xl p-4 border border-slate-200 hover:border-slate-300 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-slate-900">{apt.time}</p>
                          <p className="text-xs text-slate-500">{formatDuration(apt.service.duration)}</p>
                        </div>
                        <div className="h-12 w-px bg-slate-200" />
                        <div>
                          <p className="font-semibold text-slate-900">{apt.client.name}</p>
                          <p className="text-sm text-slate-600">{apt.service.name}</p>
                          <p className="text-sm text-blue-600 font-medium">{formatPrice(apt.service.price)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <a
                          href={`https://wa.me/55${apt.client.phone.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        >
                          <Phone size={20} />
                        </a>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          apt.status === 'confirmed' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {apt.status === 'confirmed' ? 'Confirmado' : 'Pendente'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === 'amanha' && (
            <>
              {/* Banner de lembretes */}
              {stats.pendingReminders > 0 && (
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 text-white mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Bell size={24} />
                      <div>
                        <p className="font-semibold">Enviar lembretes para amanhã</p>
                        <p className="text-sm text-amber-100">
                          {stats.pendingReminders} cliente(s) aguardando lembrete
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tomorrowAppointments.length === 0 ? (
                <div className="bg-white rounded-xl p-8 border border-slate-200 text-center">
                  <Calendar size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nenhum agendamento para amanhã</p>
                </div>
              ) : (
                tomorrowAppointments.map((apt) => {
                  const reminderSent = isReminderSent(apt.id, '24h');
                  
                  return (
                    <div 
                      key={apt.id}
                      className={`bg-white rounded-xl p-4 border transition-all ${
                        reminderSent ? 'border-green-200 bg-green-50/30' : 'border-slate-200 hover:border-amber-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-slate-900">{apt.time}</p>
                            <p className="text-xs text-slate-500">{formatDuration(apt.service.duration)}</p>
                          </div>
                          <div className="h-12 w-px bg-slate-200" />
                          <div>
                            <p className="font-semibold text-slate-900">{apt.client.name}</p>
                            <p className="text-sm text-slate-600">{apt.service.name}</p>
                            <p className="text-sm text-blue-600 font-medium">{formatPrice(apt.service.price)}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {reminderSent ? (
                            <span className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium">
                              <Check size={16} />
                              Lembrete enviado
                            </span>
                          ) : (
                            <button
                              onClick={() => handleSendReminder(apt, '24h')}
                              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                              <MessageCircle size={16} />
                              Enviar lembrete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>

        {/* Modo de lembretes */}
        <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-200">
          <div className="flex items-start gap-3">
            <MessageCircle size={20} className="text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Modo Semi-automático ativo</p>
              <p className="text-sm text-blue-700">
                Clique em &quot;Enviar lembrete&quot; para abrir o WhatsApp com a mensagem pronta. 
                É só enviar!
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-8 py-6 text-center border-t border-slate-200 bg-white">
        <p className="text-xs text-slate-400">
          Powered by <a href="/" className="text-blue-600 hover:underline">AtivaJá</a> • Agenda Cheia
        </p>
      </footer>
    </div>
  );
};

export default PainelProfissional;
